import { PageController } from '@client/components/_core/layout/PageController';
import { VTextInput } from '@client/components/_shared/forms/view-form/VTextInput';
import { DndTable } from '@client/components/_ui/data-display/DndTable';
import { RichText } from '@client/components/_ui/display/RichText';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { useAntdTable } from '@client/hooks/table/useAntdTable';
import { createFormStore } from '@client/stores/FormStore';
import { trpc } from '@client/trpc/client';
import { requiredFieldRule } from '@client/utils';
import { cloneDeep } from '@shared/utils/core/general';
import { createFileRoute } from '@tanstack/react-router';
import React, { useState } from 'react';

export const Route = createFileRoute(
    '/console/$orgId/configuration/claims-products/product-types/financials/$id/sections',
)({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Sections',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <FinancialSectionSearch />
        </PageController>
    );
}

const formStore = createFormStore<Record<string, string>>();

function FinancialSectionSearch() {
    const { id } = Route.useParams();
    const { mutate } = trpc.financialSection.reorderFinancialSections.useMutation();
    const { mutateAsync } = trpc.financialSection.updateFinancialSection.useMutation();

    const { data, isPending, refetch } = trpc.financialSection.listFinancialSections.useQuery(
        {
            where: {
                productTypeId: id,
            },
        },
        {
            select(data) {
                return {
                    rows: data.rows.map(({ id, ...item }) => ({ ...item, id: String(id) })),
                };
            },
        },
    );

    const [optimisticData, setOptimisticData] = useState(data?.rows);

    // Transform data for form store
    const formData = React.useMemo(() => {
        if (!data?.rows) {
            return undefined;
        }

        return Object.fromEntries(data.rows.map((item) => [item.id, item.title]));
    }, [data]);

    formStore.useInit({
        entityLabel: 'Financial section',
        data: formData,
        isLoading: isPending,

        async onReload() {
            await refetch();
        },

        async onSave(_, changedValues) {
            try {
                if (!data) {
                    throw new Error('Data not found.');
                }

                const [id, title] = Object.entries(changedValues)[0];

                if (!title) {
                    throw new Error('Title is required.');
                }

                const index = data.rows.findIndex((item) => item.id === id);

                if (index === -1) {
                    throw new Error(`There is no field with name ${id}.`);
                }

                const payload = cloneDeep(data.rows);
                payload[index] = { ...payload[index], title };

                await mutateAsync({
                    id: BigInt(id),
                    set: {
                        title,
                    },
                });

                await refetch();

                return { success: true };
            } catch (e) {
                return { success: false, error: e };
            }
        },
    });

    const {
        tableProps: { dataSource, ...tableProps },
    } = useAntdTable({
        rowKey: 'id',
        data: {
            loading: isPending,
            rows: optimisticData || data?.rows,
            total: (optimisticData || data?.rows)?.length,
        },
        columns: [
            {
                title: 'Display order',
                dataIndex: 'order',
                className: 'align-top',
            },
            {
                title: 'Title',
                dataIndex: 'id',
                className: 'w-40 align-top',
                render(value) {
                    return <VTextInput field={String(value)} formStore={formStore} rules={requiredFieldRule} />;
                },
            },
            {
                title: 'Description',
                dataIndex: 'description',
                className: 'max-w-xs',
                render(value) {
                    return <RichText data={value} format="tiptap" />;
                },
            },
        ],
    });

    return (
        <DndTable
            size="small"
            dataSource={dataSource}
            onDragEnd={(keys) => {
                setOptimisticData(
                    keys.map((item, i) => ({
                        ...dataSource.find(({ id }) => id === item)!,
                        order: i + 1,
                    })),
                );

                mutate(
                    {
                        productTypeId: id,
                        ids: keys.map(BigInt),
                    },
                    {
                        onSuccess() {
                            void refetch();
                        },
                        onError(error) {
                            errorMessage.show(error);
                        },
                    },
                );
            }}
            {...tableProps}
        />
    );
}
